/**
 * Created by neo on 23.03.21.
 */
import { makeAutoObservable } from 'mobx';
import { DayOfWeek, IsoDayOfWeekList } from '../../DayOfWeek';
import dayjs, { Dayjs } from 'dayjs';
import { translate } from '../../../Store/System/LocalizationStore';

function ordinalSuffix(num: number) {
  const j = num % 10;
  const k = num % 100;
  if (j === 1 && k !== 11) {
    return num + 'st';
  } else if (j === 2 && k !== 12) {
    return num + 'nd';
  } else if (j === 3 && k !== 13) {
    return num + 'rd';
  }
  return num + 'th';
}

export type RecurringPatternType = 'daily' | 'weekly' | 'monthly' | 'yearly';

export type NthDayOfMonth = 'first' | 'second' | 'third' | 'fourth' | 'last';

export type RecurringPatternJson = {
  type: RecurringPatternType;
  endDate?: string;
  maxOccurrences?: number;
  separationCount: number;
  daysOfWeek: DayOfWeek[];
  weekOfMonth?: number;
  dayOfMonth?: number;
  monthOfYear?: number;
  nthDayOfTheMonth?: NthDayOfMonth;
  weekDayOfMonth?: DayOfWeek;
};

export type RecurringPattern = RecurringPatternJson & {
  asText(time: string): string | undefined;
  calculatePossibleDates(startDateTime: Dayjs): Dayjs[];
};

export function createRecurringPattern(json?: Partial<RecurringPattern>): RecurringPattern {
  return makeAutoObservable({
    type: json?.type ?? 'daily',
    endDate: json?.endDate,
    maxOccurrences: json?.maxOccurrences,
    separationCount: json?.separationCount ?? 1,
    daysOfWeek: json?.daysOfWeek ?? [],
    weekOfMonth: json?.weekOfMonth,
    dayOfMonth: json?.dayOfMonth,
    monthOfYear: json?.monthOfYear,
    nthDayOfTheMonth: json?.nthDayOfTheMonth,
    weekDayOfMonth: json?.weekDayOfMonth,

    asText(time: string): string | undefined {
      if (this.type === 'daily') {
        return this.separationCount > 1
          ? String(translate('explore.scheduling.daily.everyNthDay', {
              time,
              ordinal: String(translate(`globals.ordinal.${this.separationCount}`, {
                defaultValue: ordinalSuffix(this.separationCount),
              })),
              defaultValue: 'Every {{ordinal}} day at {{time}}',
            }))
          : String(translate('explore.scheduling.daily.everyDay', {
              time,
              defaultValue: 'Everyday at {{time}}',
            }));
      } else if (this.type === 'weekly') {
        if ((this.daysOfWeek.length ?? 0) >= 7) {
          return this.separationCount > 1
            ? String(translate('explore.scheduling.weekly.everyDayNthWeek', {
                defaultValue: 'Every {{ordinal}} week everyday at {{time}}',
                ordinal: String(translate(`globals.ordinal.${this.separationCount}`, {
                  defaultValue: ordinalSuffix(this.separationCount),
                })),
                time,
              }))
            : String(translate('explore.scheduling.everyDay', {
                defaultValue: 'Everyday at {{time}}',
                time,
              }));
        }

        const days = this.daysOfWeek
          .slice()
          .sort((a, b) => IsoDayOfWeekList.indexOf(a) - IsoDayOfWeekList.indexOf(b))
          .map((e) => String(translate(`globals.dayOfWeek.short.${e}`, { defaultValue: e.substr(0, 2) })))
          .join(', ');
        return this.separationCount > 1
          ? String(translate('explore.scheduling.weekly.everyNthweek', {
              defaultValue: 'Every {{ordinal}} week on {{days}} at {{time}}',
              days,
              ordinal: String(translate(`globals.ordinal.${this.separationCount}`, {
                defaultValue: ordinalSuffix(this.separationCount),
              })),
              time,
            }))
          : String(translate('explore.scheduling.weekly.everyWeek', {
              defaultValue: 'Every {{days}} at {{time}}',
              days,
              time,
            }));
      } else if (this.type === 'monthly') {
        if (this.dayOfMonth) {
          return this.separationCount > 1
            ? String(translate('explore.scheduling.monthly.nthMonthDay', {
                defaultValue: 'Every {{ordinal}} month on the {{day}} at {{time}}',
                day: String(translate(`globals.ordinal.${this.dayOfMonth}`, {
                  defaultValue: ordinalSuffix(this.dayOfMonth),
                })),
                ordinal: String(translate(`globals.ordinal.${this.separationCount}`, {
                  defaultValue: ordinalSuffix(this.separationCount),
                })),
                time,
              }))
            : String(translate('explore.scheduling.monthly.day', {
                defaultValue: 'Every {{day}} at {{time}}',
                day: String(translate(`globals.ordinal.${this.dayOfMonth}`, {
                  defaultValue: ordinalSuffix(this.dayOfMonth),
                })),
                time,
              }));
        } else if (this.nthDayOfTheMonth && this.weekDayOfMonth) {
          const day = String(translate(`globals.dayOfWeek.${this.weekDayOfMonth}`, {
            defaultValue: this.weekDayOfMonth,
          }));
          return this.separationCount > 1
            ? String(translate('explore.scheduling.monthly.nthDayOfTheNthMonth', {
                defaultValue: 'Every {{ordinal}} month the {{ordinalDay}} {{day}} at {{time}}',
                day,
                ordinal: String(translate(`globals.ordinal.${this.separationCount}`, {
                  defaultValue: ordinalSuffix(this.separationCount),
                })),
                ordinalDay: String(translate(`globals.ordinalVerbal.${this.nthDayOfTheMonth}`, {
                  defaultValue: this.nthDayOfTheMonth,
                })),
                time,
              }))
            : String(translate('explore.scheduling.monthly.nthDayOfTheMonth', {
                defaultValue: 'Every {{ordinalDay}} {{day}} of the month at {{time}}',
                day,
                ordinalDay: String(translate(`globals.ordinalVerbal.${this.nthDayOfTheMonth}`, {
                  defaultValue: this.nthDayOfTheMonth,
                })),
                time,
              }));
        }
      }
      return undefined;
    },

    calculatePossibleDates(startDateTime: Dayjs): Dayjs[] {
      const dates = [startDateTime];
      const endDate = (this.endDate ? dayjs(this.endDate) : undefined) ?? startDateTime.add(1, 'year');
      const maxOccurrences = this.maxOccurrences ?? 1000;
      let currentDate = startDateTime;
      if (this.type === 'daily') {
        while (!currentDate.isAfter(endDate) && dates.length < maxOccurrences) {
          const nextDate = currentDate.add(this.separationCount, 'day');
          dates.push(nextDate.hour(startDateTime.hour()).minute(startDateTime.minute()));
          currentDate = nextDate;
        }
      } else if (this.type === 'weekly') {
        while (!currentDate.isAfter(endDate) && dates.length < maxOccurrences) {
          const endOfWeek = currentDate.endOf('isoWeek');
          this.daysOfWeek
            .map((day) => IsoDayOfWeekList.indexOf(day) + 1)
            .map((idx) => currentDate.isoWeekday(idx))
            .filter((date) => !date.isBefore(startDateTime) && !date.isAfter(endOfWeek) && !date.isAfter(endDate))
            .forEach((date) => dates.push(date.hour(startDateTime.hour()).minute(startDateTime.minute())));
          currentDate = currentDate.add(this.separationCount, 'week').startOf('isoWeek');
        }
      } else if (this.type === 'monthly') {
        while (!currentDate.isAfter(endDate) && dates.length < maxOccurrences) {
          if (this.dayOfMonth) {
            const nextDate = currentDate.add(this.separationCount, 'month').date(this.dayOfMonth);
            dates.push(nextDate);
            currentDate = nextDate;
          } else if (this.nthDayOfTheMonth && this.weekDayOfMonth) {
            const nextDate = ((start: Dayjs, nthDayOfTheMonth: NthDayOfMonth, weekDayOfMonth: DayOfWeek) => {
              let startOfMonth = start.startOf('month');
              const possibleDates: Dayjs[] = [];
              const endOfMonth = startOfMonth.endOf('month');

              while (!startOfMonth.isAfter(endOfMonth)) {
                const nextDate = startOfMonth.isoWeekday(IsoDayOfWeekList.indexOf(weekDayOfMonth) + 1);
                if (!nextDate.isAfter(endOfMonth) && !nextDate.isBefore(startOfMonth)) {
                  possibleDates.push(nextDate);
                }
                startOfMonth = startOfMonth.add(1, 'week').startOf('isoWeek');
              }

              const result = ((nthDayOfTheMonth: NthDayOfMonth, result: Dayjs[]) => {
                switch (nthDayOfTheMonth) {
                  case 'first':
                    return result[0];
                  case 'second':
                    return result[1];
                  case 'third':
                    return result[2];
                  case 'fourth':
                    return result[3];
                  default:
                    return result[result.length - 1];
                }
              })(nthDayOfTheMonth, possibleDates);

              return result?.isBefore(start) === false ? result : undefined;
            })(currentDate, this.nthDayOfTheMonth, this.weekDayOfMonth);

            if (nextDate) {
              dates.push(nextDate.hour(startDateTime.hour()).minute(startDateTime.minute()));
            }

            currentDate = currentDate.add(this.separationCount, 'month').startOf('month');
          } else {
            currentDate = endDate.add(1, 'day');
          }
        }
      }

      return dates.sort((a, b) => a.valueOf() - b.valueOf());
    },
  });
}
